<template>
  <div class="flex w-full flex-col text-gray-700">
    <h1 class="mb-4 text-2xl font-semibold">Agregar productos a la cotización #{{ preOrder.id }}</h1>
    <search-bar
      v-model="searchText"
      name="searchText"
      class="mb-3 md:mb-5"
      placeholder="Buscar por nombre de producto"
    />
    <lokal-loading
      v-if="productQueryIsLoading"
      :loading="productQueryIsLoading"
    />
    <template v-else>
      <p class="ml-auto text-sm md:text-base">{{ products?.length }} / {{ totalResources }}</p>
      <div
        v-for="product in products"
        :key="product.id"
        class="mt-2 flex items-center rounded-md border border-gray-200 p-2"
      >
        <img
          :src="product.images[0]?.derivatives?.jpgSm?.url || greenKImage"
          :alt="product.name"
          class="size-16 rounded-md"
        />
        <div class="ml-2 flex flex-col">
          <div class="flex flex-col-reverse md:flex-row md:items-center">
            <p class="line-clamp-2 text-sm font-medium hover:line-clamp-none">{{ product.name }}</p>
            <div
              class="mr-auto rounded-full px-2 py-0.5 text-xs md:ml-2 md:mr-0 md:text-sm"
              :class="{
                'bg-green-100 text-green-700': product.status === 'active',
                'bg-red-100 text-red-700': product.status === 'inactive',
              }"
            >
              {{ product.status }}
            </div>
          </div>
          <p class="text-xs text-gray-500">{{ product.sku }}</p>
        </div>
        <div class="relative ml-auto flex w-24 items-center justify-center">
          <div
            v-if="!selectedProducts[product.id]"
            class="flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-700 transition-all duration-300 ease-in-out"
          >
            <button
              class="flex items-center justify-center"
              @click="addProduct(product)"
            >
              <PlusIcon class="size-4" />
            </button>
          </div>
          <div
            v-else-if="selectedProducts[product.id] >= 1"
            class="group relative flex size-8 w-24 items-center justify-center overflow-hidden rounded-full bg-gray-100 p-1 text-gray-700 transition-all duration-300 ease-in-out"
          >
            <div class="absolute inset-0 flex items-center justify-center">
              <button
                class="rounded-l-full px-2"
                @click="decrementProduct(product)"
              >
                <MinusIcon class="size-4" />
              </button>
              <Field
                v-model="selectedProducts[product.id]"
                :name="`selectedProducts[${product.id}]`"
                class="w-8 bg-gray-100 text-center text-sm focus:outline-none"
              />
              <button
                class="rounded-r-full px-2"
                @click="incrementProduct(product)"
              >
                <PlusIcon class="size-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <pagination-component
      v-if="!productQueryIsLoading && products?.length && totalPages > 1"
      :total-pages="totalPages"
      :current-page="currentProductsPage"
      @set-new-page="updateNewPage"
    />
    <div class="mx-auto flex w-full flex-col items-center md:flex-row-reverse md:justify-between">
      <button
        v-if="Object.keys(selectedProducts).length > 0"
        class="flex w-auto items-center rounded-md bg-lk-green p-2 text-white hover:bg-lk-green-deep"
        @click="emit('move-to:share')"
      >
        <span class="text-sm md:text-base">Avanzar a compartir</span>
        <ArrowRightIcon class="ml-2 size-4 md:size-5" />
      </button>
      <button
        class="mt-1 hover:underline md:mt-0"
        @click="emit('move-to:create-update')"
      >
        <span class="text-sm md:text-base">Volver a editar</span>
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useMutation } from 'vue-query';
import { Field } from 'vee-validate';
import { PlusIcon, MinusIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';
import preOrderProductsApi from 'api/pre-order-products';
import greenKImage from 'assets/images/green-k.jpg';
import useProductsQuery from './queries/products-query';
import searchBar from './shared/search-bar.vue';
import PaginationComponent from './shared/pagination-component.vue';
import useMessageStore from '../stores/message-store';

const props = defineProps<{
  maker: Maker;
  preOrder: PreOrder;
  preOrderProducts: PreOrderProduct[];
}>();

const messageStore = useMessageStore();

const selectedProducts = ref({} as Record<number, number>);
watch(
  () => props.preOrderProducts,
  (newPreOrderProducts) => {
    newPreOrderProducts.forEach((preOrderProduct) => {
      selectedProducts.value[preOrderProduct.productId] = preOrderProduct.quantity;
    });
  },
  { immediate: true },
);

function addProduct(product: Product) {
  const minimumRequired = Math.max(product.packUnits, product.minimumPurchaseQuantity);
  createPreOrderProductMutation.mutate({
    preOrderId: props.preOrder.id,
    productId: product.id,
    quantity: minimumRequired,
  });
}

function incrementProduct(product: Product) {
  if (selectedProducts.value[product.id]) {
    createPreOrderProductMutation.mutate({
      preOrderId: props.preOrder.id,
      productId: product.id,
      quantity: selectedProducts.value[product.id] + product.packUnits,
    });
  } else {
    addProduct(product);
  }
}

function decrementProduct(product: Product) {
  if (selectedProducts.value[product.id] === product.minimumPurchaseQuantity) {
    deletePreOrderProductMutation.mutate({
      preOrderId: props.preOrder.id,
      productId: product.id,
    });
  } else {
    createPreOrderProductMutation.mutate({
      preOrderId: props.preOrder.id,
      productId: product.id,
      quantity: selectedProducts.value[product.id] - product.packUnits,
    });
  }
}

const searchText = ref('');
const queryParams = computed(() => {
  let params = `q[maker_id_eq]=${props.maker.id}&q[s][]=status+desc&q[s][]=maker_priority+asc`;
  if (searchText.value) {
    params += `&q[name_cont]=${searchText.value}`;
  }

  return params;
});
const currentProductsPage = ref(1);
const { products, productQueryIsLoading, totalResources, totalPages } = useProductsQuery(
  currentProductsPage,
  queryParams,
  computed(() => !!props.preOrder.id),
);

function updateNewPage(page: number) {
  currentProductsPage.value = page;
}

const createPreOrderProductMutation = useMutation(
  (data: Partial<PreOrderProduct>) => preOrderProductsApi.createOrUpdate(data),
  {
    onSuccess: (data) => {
      const preOrderProduct = data.data.preOrderProduct as PreOrderProduct;
      selectedProducts.value[preOrderProduct.productId] = preOrderProduct.quantity;
      const selectedProduct = products.value?.find((product) => product.id === preOrderProduct.productId);
      if (selectedProduct) {
        messageStore.showMessage(
          `Producto ${selectedProduct.name} agregado a la cotización con cantidad ${preOrderProduct.quantity}`,
          'success',
        );
      } else {
        messageStore.showMessage(
          `Producto agregado a la cotización con cantidad ${preOrderProduct.quantity}`,
          'success',
        );
      }
    },
  },
);

const deletePreOrderProductMutation = useMutation(
  ({ preOrderId, productId }: { preOrderId: number; productId: number }) =>
    preOrderProductsApi.destroy(preOrderId, productId),
  {
    onSuccess: (_, { productId }) => {
      const selectedProduct = products.value?.find((product) => product.id === productId);
      delete selectedProducts.value[productId];
      if (selectedProduct) {
        messageStore.showMessage(`Producto ${selectedProduct.name} eliminado de la cotización`, 'success');
      } else {
        messageStore.showMessage('Producto eliminado de la cotización', 'success');
      }
    },
  },
);

const emit = defineEmits<{
  'move-to:share': [];
  'move-to:create-update': [];
}>();
</script>
